/**
 * jai1 tasks list [-s status] [-P parent] [-j]
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { TaskService } from '../../services/task.service.js';
import {
    STATUS_ICONS,
    BLOCKED_ICON,
    PRIORITY_ICONS,
    type Task,
    type TaskListOptions,
    type TaskStatusType,
} from '../../types/task.types.js';

export function createTaskListCommand(): Command {
    return new Command('list')
        .alias('ls')
        .description('List tasks')
        .option('-s, --status <status>', 'Filter by status: todo, in_progress, done, cancelled')
        .option('-P, --parent <parent>', 'Filter by parent: feature/xxx, plan/xxx')
        .option('-j, --json', 'Output JSON')
        .action(async (options: TaskListOptions) => {
            await handleTaskList(options);
        });
}

async function handleTaskList(options: TaskListOptions): Promise<void> {
    const service = new TaskService();
    const allTasks = await service.readAll();

    let tasks = allTasks;

    if (options.status) {
        tasks = tasks.filter((t) => t.status === options.status);
    }
    if (options.parent) {
        tasks = tasks.filter((t) => t.parent === options.parent);
    }

    if (options.json) {
        console.log(JSON.stringify(tasks, null, 2));
        return;
    }

    if (tasks.length === 0) {
        console.log(chalk.dim('No tasks found.'));
        return;
    }

    // Compute blocked status (done or cancelled = resolved)
    const resolvedIds = new Set(allTasks.filter((t) => t.status === 'done' || t.status === 'cancelled').map((t) => t.id));

    const header = options.parent
        ? `📋 ${options.parent} (${tasks.length} tasks)`
        : `📋 All tasks (${tasks.length})`;
    console.log(chalk.bold(header));
    console.log();

    for (const task of tasks) {
        printTaskLine(task, resolvedIds);
    }
}

function printTaskLine(task: Task, resolvedIds: Set<string>): void {
    const isBlocked =
        task.status === 'todo' &&
        task.depends_on.length > 0 &&
        !task.depends_on.every((id) => resolvedIds.has(id));

    const statusIcon = isBlocked ? BLOCKED_ICON : STATUS_ICONS[task.status as TaskStatusType] || '📋';
    const priorityIcon = PRIORITY_ICONS[task.priority] || '🟡';

    let line = `  ${statusIcon}  ${chalk.dim(task.id)}  P${task.priority}${priorityIcon}  ${task.title}`;

    if (task.status === 'in_progress' && task.assigned_to) {
        line += chalk.cyan(` @${task.assigned_to}`);
    }

    if (isBlocked) {
        const blockedBy = task.depends_on.filter((id) => !resolvedIds.has(id));
        line += chalk.red(` (blocked: ${blockedBy.join(', ')})`);
    }

    // Show type if not default 'task'
    if (task.type && task.type !== 'task') {
        line += chalk.magenta(` {${task.type}}`);
    }

    if (task.parent) {
        line += chalk.dim(` [${task.parent}]`);
    }

    console.log(line);
}
